import get from "lodash/get";
import { ChangeEvent, FocusEvent, MouseEvent, useEffect, RefObject } from "react";
import { Logger } from "@utils/logger";
import { FormikErrors } from 'formik';
import { AttributeFormState } from "@idw-types/state/attribute-form-state";
import { useParamSelector } from "@hooks/use-param-selector";
import { getAttempt, shouldHighlightInput } from "@redux/selectors/verification-form-selectors";
import { AXNUITextInputWithErrorMessage } from '@iddataweb/idw-common-ui/containers';
import { useSelector } from "react-redux";
import { AttributeStatusTypes } from "@enums/attribute-status-types";
import classNames from "classnames";
import styles from "./pin-input.less";
import { getLanguageContent } from "@redux/selectors/language-selectors";

type PinInputProps = {
    attribute: AttributeFormState;
    fieldName: string;
    fieldTouched: boolean;
    fieldError: string;
    isSubmitting: boolean;
    isALink: boolean;
    canResendPin: boolean;
    sendingPin: boolean;
    resendEnabled: boolean;
    inputRef: RefObject<HTMLInputElement>;
    onBlur: (event: FocusEvent<HTMLInputElement>) => void;
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
    onSetSubmitValue: (data: any) => void;
    sendPin: (event: MouseEvent<HTMLAnchorElement>) => void;
};

export const PinInput = (props: PinInputProps) => {
    const {
        attribute,
        fieldName,
        fieldTouched,
        fieldError,
        isSubmitting,
        isALink,
        canResendPin,
        sendingPin,
        resendEnabled,
        inputRef,
        onBlur,
        setFieldTouched,
        setFieldValue,
        onSetSubmitValue,
        sendPin,
    } = props;

    const attempt = useSelector(getAttempt);
    const content = useSelector(getLanguageContent);

    const initializePinInput = async () => {
        await setFieldValue(fieldName, "");
        setFieldTouched(fieldName, false, true);
    };

    const showInvalidPinMessage = async () => {
        await setFieldValue(fieldName, "");
        setFieldTouched(fieldName, true, true);
    };

    useEffect(() => {
        initializePinInput();
    }, []);

    useEffect(() => {
        if (attribute.hasInvalidPin && attribute.status !== AttributeStatusTypes.PASS) {
            showInvalidPinMessage();
        }
    }, [attribute.hasInvalidPin, attribute.status, attempt]);

    const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value?.length < 7) {
            Logger.info('[PINInput] submit value: ', value);
            const inputData = { ...attribute.inputData, pincode: value };
            onSetSubmitValue({ inputData });
            await setFieldValue(fieldName, value);
            setFieldTouched(fieldName, true, true);
        }
    };

    const getResendButtonText = () => {
        if (sendingPin) {
            return content.form.pinInput.sendingPin;
        } else if (resendEnabled) {
            return content.form.pinInput.resendPin;
        } else {
            return content.form.pinInput.pinSent;
        }
    };

    const fieldErrorMessage = fieldError ? (get(content, fieldError) || fieldError) : null;
    const fieldIsInvalid = fieldTouched && !!fieldError;
    const fieldShouldBeHighlighted = useParamSelector(shouldHighlightInput, attribute);
    const placeholderString = content.form.pinInput.inputPlaceholder.replace("{{label}}", attribute.label);

    return (
        <div className={classNames(styles.pinInput, "pin-input-container")} id={`${attribute.id}-responses`}>
            {attribute.inBandVerificationFailed && (
                <label htmlFor={`attr${attribute.id}-PIN-input`} id={`attr${attribute.id}-PIN-label`}>
                    {attribute.customError
                        ? attribute.customError
                        : content.form.pinInput.responseErrorMessage
                    }
                </label>
            )}
            <AXNUITextInputWithErrorMessage
                autocomplete={"off"}
                controlId={`attr${attribute.id}-PIN-input`}
                automationId={`attr${attribute.id}-PIN-input`}
                fieldName={fieldName}
                fieldError={fieldErrorMessage}
                fieldTouched={fieldTouched}
                fieldIsInvalid={fieldIsInvalid}
                fieldShouldBeHighlighted={fieldShouldBeHighlighted}
                setFieldTouched={setFieldTouched}
                onBlur={onBlur}
                isDisabled={attribute.isDisabled}
                isPlaceholder={attribute.isPlaceholder}
                isOptional={attribute.optional}
                isSubmitting={isSubmitting}
                onChange={handleChange}
                inputRef={inputRef}
                //@ts-ignore
                title={undefined}
                //@ts-ignore
                placeholder={placeholderString}
                value={attribute.inputData.pincode}
                inputErrorMessageClassName={"invalid-feedback"}
            />
            <small>
                {isALink && !canResendPin && (
                    <span className={"float-right"}>
                        {content.form.pinInput.unableToResendPin}
                    </span>
                )}
                {isALink && canResendPin && (
                    <a
                        id={`attr${attribute.id}-verify-pin-resend`}
                        className={`pin-related float-right`}
                        href={""}
                        onClick={sendPin}
                    >
                        {getResendButtonText()}
                    </a>
                )}
                {!isALink && (
                    <span className={"float-right"}>{getResendButtonText()}</span>
                )}
            </small>
        </div>
    );
};